import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layers, Plus } from 'lucide-react';
import { formatNumber, formatShortDate, messages, type ListSummary } from '@nesc/shared';
import { api, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { ResponsiveList, RowChevron, type Column } from '@/components/common/responsive-list';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { ListFormDialog } from '@/features/lists/list-form-dialog';
import { ListTypeBadge } from '@/features/lists/list-type-badge';

/** S32 リスト一覧。静的リスト・条件リストの管理（キャンペーンとは独立） */
export const ListsPage = () => {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.lists,
    queryFn: () => api.get<SingleResponse<ListSummary[]>>('/lists'),
  });
  const rows = data?.data ?? [];

  const columns: Column<ListSummary>[] = [
    {
      key: 'name',
      header: 'リスト名',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{row.name}</p>
          {row.description && (
            <p className="truncate text-note text-foreground-subtle">{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: '種類',
      width: '112px',
      render: (row) => <ListTypeBadge type={row.type} />,
    },
    {
      key: 'companyCount',
      header: '企業数',
      width: '96px',
      align: 'right',
      render: (row) => formatNumber(row.companyCount),
    },
    {
      key: 'usedBy',
      header: 'キャンペーン',
      width: '128px',
      align: 'right',
      render: (row) =>
        row.usedByCampaignCount > 0 ? (
          `${formatNumber(row.usedByCampaignCount)} 件で使用`
        ) : (
          <span className="text-foreground-subtle">—</span>
        ),
    },
    {
      key: 'createdBy',
      header: '作成者',
      width: '120px',
      render: (row) => row.createdBy?.name ?? <span className="text-foreground-subtle">—</span>,
    },
    {
      key: 'updatedAt',
      header: '更新',
      width: '96px',
      render: (row) => <span className="tabular">{formatShortDate(row.updatedAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title={messages.list.title}
        description={
          data ? <span className="tabular">{formatNumber(rows.length)} 件</span> : undefined
        }
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            {messages.list.create}
          </Button>
        }
      />

      <ResponsiveList
        columns={columns}
        rows={rows}
        layout="fixed"
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/lists/${row.id}`)}
        empty={
          <EmptyState
            title={messages.empty.lists.title}
            description={messages.empty.lists.description}
            action={{ label: messages.empty.lists.action, onClick: () => navigate('/companies') }}
            icon={<Layers className="size-8" />}
          />
        }
        card={(row) => (
          <button type="button" className="w-full text-left" onClick={() => navigate(`/lists/${row.id}`)}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate text-body font-semibold">{row.name}</span>
                  <ListTypeBadge type={row.type} size="lg" />
                </div>
                {row.description && (
                  <p className="truncate text-note text-foreground-muted">{row.description}</p>
                )}
                <p className="text-note text-foreground-muted">
                  <span className="tabular">{formatNumber(row.companyCount)} 社</span>
                  {row.usedByCampaignCount > 0 && ` ・ キャンペーン ${formatNumber(row.usedByCampaignCount)} 件`}
                  {' ・ 更新 '}
                  <span className="tabular">{formatShortDate(row.updatedAt)}</span>
                </p>
              </div>
              <RowChevron />
            </div>
          </button>
        )}
      />

      <ListFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(list) => navigate(`/lists/${list.id}`)}
      />
    </>
  );
};
